import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Location } from '../types/api';
import { useSelectedLocation, useUpdateLocation } from '../hooks/useApi';

interface LocationContextType {
  selectedLocation: Location | null;
  setSelectedLocation: (location: Location | null) => void;
  /** Set location in state only (no API call). Use when location came from navigation state to avoid 403 loop. */
  setSelectedLocationOptimistic: (location: Location | null) => void;
  loading: boolean;
  error: string | null;
  isInitialized: boolean;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

export const LocationProvider = ({ children }: { children: ReactNode }) => {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { data: backendLocation, loading: loadingLocation, error: locationError, refetch: refetchSelectedLocation } = useSelectedLocation();
  const { updateLocation, loading: updatingLocation, error: updateError } = useUpdateLocation();

  const persistSelectedLocationId = (location: Location | null) => {
    if (location?.id) {
      localStorage.setItem('selected_location_id', location.id);
    } else {
      localStorage.removeItem('selected_location_id');
    }
  };

  // Load location from backend on mount
  useEffect(() => {
    if (!loadingLocation) {
      setSelectedLocation(backendLocation || null);
      persistSelectedLocationId(backendLocation || null);
      setIsInitialized(true);
    }
  }, [backendLocation, loadingLocation]);

  // Handle setting location - update both local state and backend, then refetch so Layout sees correct state
  const handleSetSelectedLocation = async (location: Location | null) => {
    try {
      setSelectedLocation(location);
      persistSelectedLocationId(location);
      await updateLocation(location?.id || null);
      await refetchSelectedLocation();
    } catch (error) {
      console.error('Error updating location:', error);
      // Revert local state on error
      setSelectedLocation(backendLocation ?? null);
      persistSelectedLocationId(backendLocation ?? null);
    }
  };

  // Optimistic-only: set location in state without calling API (stops 403 loop when opening dashboard from /locations state)
  const setSelectedLocationOptimistic = (location: Location | null) => {
    setSelectedLocation(location);
    persistSelectedLocationId(location);
  };

  return (
    <LocationContext.Provider
      value={{
        selectedLocation,
        setSelectedLocation: handleSetSelectedLocation,
        setSelectedLocationOptimistic,
        loading: loadingLocation || updatingLocation,
        error: locationError || updateError,
        isInitialized,
      }}
    >
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};